import { PayloadAction, combineReducers, createSlice } from '@reduxjs/toolkit'

import {
  loadClientParallelLoans,
  loadClientRelatedLoans,
  loadClientLoans,
  clientSearch,
  clientSearchAgain,
  checkReqFields,
  destroyReqFields,
} from './client.actions'
import { ClientSearchAttributes } from '@lk/api'
export interface ClientLoans {
  status: 'idle' | 'loading' | 'loaded' | 'failed'
  error: string
  data: Array<any>
}

const initialClientLoansState: ClientLoans = {
  status: 'idle',
  error: null,
  data: [],
}

export const clientLoans = createSlice({
  name: 'clientLoans',
  initialState: initialClientLoansState,
  reducers: {},
  extraReducers: {
    [loadClientLoans.pending.toString()]: (state) => {
      state.status = 'loading'
      state.data = []
    },
    [loadClientLoans.fulfilled.toString()]: (state, action) => {
      state.status = 'loaded'
      state.data = action.payload
    },
    [loadClientLoans.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
      state.data = []
    },
  },
}).reducer

export interface ParallelLoans {
  status: 'idle' | 'loading' | 'loaded' | 'failed'
  error: string
  byId: {
    [key: string]: Array<any>
  }
}

const initialParallelLoansState: ParallelLoans = {
  status: 'idle',
  error: null,
  byId: {},
}

export const parallelLoansById = createSlice({
  name: 'parallelLoansById',
  initialState: initialParallelLoansState,
  reducers: {},
  extraReducers: {
    [loadClientParallelLoans.pending.toString()]: (state) => {
      state.status = 'loading'
    },
    [loadClientParallelLoans.fulfilled.toString()]: (state, action) => {
      state.status = 'loaded'
      state.byId[action.payload.id] = action.payload.loans
    },
    [loadClientParallelLoans.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
    },
  },
}).reducer

export interface RelatedLoans {
  status: 'idle' | 'loading' | 'loaded' | 'failed'
  error: string
  byId: {
    [key: string]: Array<any>
  }
}

const initialRelatedLoansState: RelatedLoans = {
  status: 'idle',
  error: null,
  byId: {},
}

export const relatedLoansById = createSlice({
  name: 'relatedLoansById',
  initialState: initialRelatedLoansState,
  reducers: {},
  extraReducers: {
    [loadClientRelatedLoans.pending.toString()]: (state) => {
      state.status = 'loading'
    },
    [loadClientRelatedLoans.fulfilled.toString()]: (state, action) => {
      state.status = 'loaded'
      state.byId[action.payload.id] = action.payload.loans
    },
    [loadClientRelatedLoans.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
    },
  },
}).reducer

export interface ClientData {
  status: 'idle' | 'loading' | 'loaded' | 'failed'
  error: string
  data: {
    [key: string]: Array<any>
  }
  prevSearchData: {
    [key: string]: ClientSearchAttributes
  }
}

const clientDataState: ClientData = {
  status: 'idle',
  error: null,
  data: {},
  prevSearchData: {},
}

export const clientDataSlice = createSlice({
  name: 'clientData',
  initialState: clientDataState,
  reducers: {
    destroyClientData: (state, action: PayloadAction<string>) => {
      state.data[action.payload] = []
    },
  },
  extraReducers: {
    [clientSearch.pending.toString()]: (state) => {
      state.status = 'loading'
    },
    [clientSearch.fulfilled.toString()]: (state, action) => {
      state.status = 'loaded'
      if (action.payload.instanceName) {
        state.prevSearchData[action.payload.instanceName] = action.meta.arg
        state.data[action.payload.instanceName] = action.payload.data
      }
    },
    [clientSearch.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
    },
    [clientSearchAgain.pending.toString()]: (state) => {
      state.status = 'loading'
    },
    [clientSearchAgain.fulfilled.toString()]: (state, action) => {
      state.status = 'loaded'
      if (action.payload.instanceName) {
        state.data[action.payload.instanceName] = action.payload.data
      }
    },
    [clientSearchAgain.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
    },
  },
})

export const { destroyClientData } = clientDataSlice.actions

type CheckReqFields = {
  status: 'idle' | 'loading' | 'loaded' | 'failed'
  error: string | null
  data: unknown[]
}

const initialReqFieldsState: CheckReqFields = {
  status: 'idle',
  error: null,
  data: null,
}

export const clientrReqFieldsSlice = createSlice({
  name: 'clientReqFields',
  initialState: initialReqFieldsState,
  reducers: {},
  extraReducers: {
    [checkReqFields.pending.toString()]: (state) => {
      state.status = 'loading'
      state.data = null
      state.error = null
    },
    [checkReqFields.fulfilled.toString()]: (state, action) => {
      state.status = 'loaded'
      state.data = action.payload
    },
    [checkReqFields.rejected.toString()]: (state, action) => {
      state.status = 'failed'
      state.error = action.payload
      state.data = null
    },
    [destroyReqFields.toString()]: () => {
      return initialReqFieldsState
    },
  },
})

export const clientReducer = combineReducers({
  clientLoans,
  parallelLoans: parallelLoansById,
  relatedLoans: relatedLoansById,
  clientData: clientDataSlice.reducer,
  reqFields: clientrReqFieldsSlice.reducer,
})
